import type { Dispatch } from 'react';

import { parseNumber } from './format.js';
import { getI18nErrorMessage } from '../../forms-core/hooks/useFormFieldValidation.js';
import {
  FormFieldMessagesAction,
  type FormFieldMessagesReducerAction,
} from '../../forms-core/state/formfield-messages-state-reducer.js';

/**
 * Sanitizes a numeric step value, returning 1 if the value is undefined, NaN, zero, or negative.
 * @internal
 */
export function sanitizePositiveNumber(value: number | undefined): number {
  return value !== undefined && !Number.isNaN(value) && value > 0 ? value : 1;
}

/**
 * Checks if the given value is aligned with the specified step and minimum value.
 * @internal
 */
export function isStepAligned(
  value: number,
  step: number,
  min: number = 0,
): boolean {
  step = sanitizePositiveNumber(step);
  const roundedValue = Math.round((value - min) / step) * step + min;
  const tolerance = step * 1e-9;
  return Math.abs(value - roundedValue) < tolerance;
}

/**
 * Adds an error message to the form field messages state.
 * @internal
 */
export function addError(
  errorType: keyof ValidityState,
  errorId: string,
  ref: HTMLInputElement | HTMLSelectElement | null | undefined,
  dispatch: Dispatch<FormFieldMessagesReducerAction> | undefined,
): void {
  dispatch?.({
    type: FormFieldMessagesAction.ADDMESSAGE,
    payload: {
      id: errorId,
      message: getI18nErrorMessage(errorType, ref),
      validityStateKey: errorType,
      messageType: 'formcontrol',
      variant: 'error',
    },
  });
}

/**
 * Determines the appropriate error key based on the provided value and validation constraints (min, max, step).
 * @internal
 */
export function getErrorKey(
  value: string | number,
  min?: number,
  max?: number,
  step?: number,
): keyof ValidityState | null {
  const parsed = parseNumber(value);

  if (min !== undefined && parsed < min) {
    return 'rangeUnderflow';
  }

  if (max !== undefined && parsed > max) {
    return 'rangeOverflow';
  }

  if (step && step > 0 && !isStepAligned(parsed, step, min ?? 0)) {
    return 'stepMismatch';
  }
  return null;
}
